<template>
  <div class="doc-record">
    <!-- 页头 -->
    <div class="page-head">
      <div class="page-head-left">
        <h2 class="page-title">竞标记录</h2>
        <p class="page-desc">按项目归集的竞标明细，掌握每场角逐的胜负与报价</p>
      </div>
      <div class="page-head-right">
        <el-input
          v-model.trim="search"
          placeholder="搜索项目名称 / 投标单位 / 项目编号"
          prefix-icon="el-icon-search"
          clearable
          style="width: 300px"
          @clear="handleSearch"
          @keyup.enter.native="handleSearch"
        ></el-input>
        <el-button type="danger" plain icon="el-icon-delete" @click="handleDeleteRecords">删除数据记录</el-button>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-grid">
      <div v-for="card in statCards" :key="card.key" class="stat-card" :class="'tone-' + card.tone">
        <div class="stat-info">
          <div class="stat-label">{{ card.label }}</div>
          <div class="stat-value">
            <span class="stat-num">{{ card.display }}</span>
            <span v-if="card.unit" class="stat-unit">{{ card.unit }}</span>
          </div>
        </div>
        <div class="stat-icon"><i :class="card.icon"></i></div>
      </div>
    </div>

    <!-- 竞标记录明细 -->
    <div class="panel">
      <div class="panel-head">
        <h3>竞标记录明细</h3>
        <span class="panel-note">仅展示存在竞标对手的项目 · 共 {{ groups.length }} 项</span>
      </div>
      <div class="panel-body">
        <el-table :data="pagedGroups" stripe size="small" style="width: 100%" class="record-table">
          <el-table-column label="项目名称" min-width="240">
            <template slot-scope="scope">
              <div class="group-project">
                <div class="group-name">{{ scope.row.project }}</div>
                <div class="group-meta">
                  <i class="el-icon-office-building"></i>{{ scope.row.procurement }}
                  <template v-if="scope.row.remark"> · <span class="group-remark">{{ scope.row.remark }}</span></template>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="竞标公司" min-width="220">
            <template slot-scope="scope">
              <div class="bidder-list">
                <div v-for="(b, i) in orderedBidders(scope.row)" :key="i" class="bidder-line">
                  <span class="bidder-dot" :class="b.won ? 'is-won' : ''"></span>
                  <span class="bidder-name" :class="{ 'is-won': b.won }">{{ b.unit }}</span>
                  <el-tag v-if="b.won" type="success" size="mini" effect="light">中标</el-tag>
                  <span v-if="b.won && b.price" class="bidder-won-price">{{ formatMoney(b.price) }}</span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="投标报价" width="140" align="right">
            <template slot-scope="scope">
              <div class="bidder-list price-list">
                <div v-for="(b, i) in orderedBidders(scope.row)" :key="i" class="bidder-line">
                  <span class="bidder-price" :class="{ 'is-won': b.won }">{{ b.price ? formatMoney(b.price) : '—' }}</span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="是否中标" width="110" align="center">
            <template slot-scope="scope">
              <el-tag v-if="groupWinner(scope.row)" type="success" effect="light" size="small">
                <i class="el-icon-check"></i> 成功中标
              </el-tag>
              <el-tag v-else type="info" effect="plain" size="small">未中标</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="中标金额" width="110" align="right">
            <template slot-scope="scope">
              <span class="win-amount">{{ formatWonAmount(groupWinner(scope.row)) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="开标日期" width="150">
            <template slot-scope="scope">
              <span class="date-cell">{{ scope.row.bidDate || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" icon="el-icon-edit" @click="showEdit(scope.row)">编辑</el-button>
            </template>
          </el-table-column>
          <template slot="empty">
            <div class="table-empty">
              <i class="el-icon-trophy"></i>
              <p>暂无可展示的竞标记录</p>
            </div>
          </template>
        </el-table>

        <div class="pager-wrap">
          <el-pagination
            background
            :current-page.sync="page"
            :page-size.sync="pageSize"
            :page-sizes="[8, 10, 20]"
            :total="groups.length"
            layout="total, sizes, prev, pager, next, jumper"
          ></el-pagination>
        </div>
      </div>
    </div>

    <!-- 图表区 -->
    <div class="chart-grid">
      <div class="panel">
        <div class="panel-head">
          <h3>竞标胜率趋势</h3>
          <span class="panel-note">月度中标率 %</span>
        </div>
        <div class="panel-body">
          <div ref="winRateChart" class="chart chart-lg"></div>
        </div>
      </div>
      <div class="panel">
        <div class="panel-head">
          <h3>竞标结果分布</h3>
          <span class="panel-note">全部项目</span>
        </div>
        <div class="panel-body">
          <div ref="resultPieChart" class="chart chart-lg"></div>
        </div>
      </div>
    </div>

    <!-- 编辑竞标单位 -->
    <bidder-edit-dialog
      :visible.sync="editVisible"
      :project-info="editProjectInfo"
      @saved="handleSaved"
    ></bidder-edit-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { RAW_DATA, formatMoney, groupByProject, monthlyStats } from './bidData'
import BidderEditDialog from './BidderEditDialog.vue'

export default {
  name: 'DocumentRecord',
  components: { BidderEditDialog },
  data() {
    return {
      rawData: RAW_DATA,
      search: '',
      page: 1,
      pageSize: 8,
      editVisible: false,
      editProjectInfo: { project: '', procurement: '', bidDate: '' },
      winRateChartInst: null,
      resultPieChartInst: null
    }
  },
  computed: {
    filteredRaw() {
      const kw = this.search.toLowerCase()
      if (!kw) return this.rawData.slice()
      return this.rawData.filter(d => {
        const name = (d['项目名称'] || '').toLowerCase()
        const unit = (d['投标单位'] || '').toLowerCase()
        const no = (d['项目编号'] || '').toLowerCase()
        return name.indexOf(kw) !== -1 || unit.indexOf(kw) !== -1 || no.indexOf(kw) !== -1
      })
    },
    groups() {
      return groupByProject(this.filteredRaw).filter(g => g.bidders.length > 1)
    },
    pagedGroups() {
      const start = (this.page - 1) * this.pageSize
      return this.groups.slice(start, start + this.pageSize)
    },
    statCards() {
      const raw = this.filteredRaw
      const total = raw.length
      const won = raw.filter(d => d['是否中标'] === '是').length
      const lost = raw.filter(d => d['是否中标'] === '否').length
      const wonPrices = raw
        .filter(d => d['是否中标'] === '是' && !isNaN(parseFloat(d['报价'])))
        .map(d => parseFloat(d['报价']))
      const avg = wonPrices.length ? wonPrices.reduce((s, v) => s + v, 0) / wonPrices.length : 0
      const avgDisplay = avg >= 10000 ? (avg / 10000).toFixed(0) : avg.toFixed(1)
      const avgUnit = avg >= 10000 ? '万' : '元'
      return [
        { key: 'total', label: '总竞标场次', value: total, display: String(total), unit: '', icon: 'el-icon-tickets', tone: 'blue' },
        { key: 'won', label: '中标场次', value: won, display: String(won), unit: '', icon: 'el-icon-circle-check', tone: 'green' },
        { key: 'lost', label: '未中标场次', value: lost, display: String(lost), unit: '', icon: 'el-icon-circle-close', tone: 'red' },
        { key: 'avg', label: '平均中标价', value: avg, display: avgDisplay, unit: avgUnit, icon: 'el-icon-coin', tone: 'cyan' }
      ]
    }
  },
  watch: {
    search() { this.page = 1 }
  },
  mounted() {
    this.$nextTick(() => {
      this.renderWinRateChart()
      this.renderResultPieChart()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.winRateChartInst) this.winRateChartInst.dispose()
    if (this.resultPieChartInst) this.resultPieChartInst.dispose()
  },
  methods: {
    formatMoney,
    orderedBidders(group) {
      // 主投标单位在前，竞争单位在后
      return group.bidders.slice().sort((a, b) => {
        if (!a.isCompetitor && b.isCompetitor) return -1
        if (a.isCompetitor && !b.isCompetitor) return 1
        return 0
      })
    },
    groupWinner(group) {
      return group.bidders.find(b => b.won) || null
    },
    formatWonAmount(winner) {
      if (!winner || !winner.price) return '—'
      const n = parseFloat(winner.price)
      if (isNaN(n)) return '—'
      if (n >= 10000) return Math.round(n / 10000) + '万'
      return n.toLocaleString('zh-CN')
    },
    handleSearch() {
      this.page = 1
    },
    showEdit(group) {
      this.editProjectInfo = {
        project: group.project,
        procurement: group.procurement,
        bidDate: group.bidDate
      }
      this.editVisible = true
    },
    handleSaved() {
      this.rawData = RAW_DATA.slice()
    },
    handleDeleteRecords() {
      this.$confirm('此操作将删除全部竞标记录数据（演示环境仅重置视图），是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$message.success('已删除数据记录')
      }).catch(() => {})
    },
    renderWinRateChart() {
      if (this.winRateChartInst) this.winRateChartInst.dispose()
      const el = this.$refs.winRateChart
      if (!el) return
      const stats = monthlyStats(this.rawData)
      this.winRateChartInst = echarts.init(el)
      this.winRateChartInst.setOption({
        tooltip: {
          trigger: 'axis',
          formatter: p => {
            const s = stats[p[0].dataIndex]
            return `${s.month}<br/>胜率：${s.winRate}%<br/>投标 ${s.total} 场 · 中标 ${s.won} 场`
          }
        },
        grid: { left: 12, right: 20, top: 20, bottom: 8, containLabel: true },
        xAxis: {
          type: 'category',
          data: stats.map(s => s.month),
          axisLine: { lineStyle: { color: '#dcdfe6' } },
          axisLabel: { color: '#909399' }
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: 100,
          splitLine: { lineStyle: { color: '#f0f2f5' } },
          axisLabel: { color: '#909399', formatter: '{value}%' }
        },
        series: [{
          name: '胜率',
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 7,
          data: stats.map(s => s.winRate),
          lineStyle: { color: '#409eff', width: 3 },
          itemStyle: { color: '#409eff', borderColor: '#fff', borderWidth: 2 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(64,158,255,0.28)' },
              { offset: 1, color: 'rgba(64,158,255,0)' }
            ])
          },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'dashed', color: '#c0c4cc' },
            label: { show: false },
            data: [{ yAxis: 50 }]
          }
        }]
      })
    },
    renderResultPieChart() {
      if (this.resultPieChartInst) this.resultPieChartInst.dispose()
      const el = this.$refs.resultPieChart
      if (!el) return
      const won = this.rawData.filter(d => d['是否中标'] === '是').length
      const total = this.rawData.length
      this.resultPieChartInst = echarts.init(el)
      this.resultPieChartInst.setOption({
        color: ['#36cf63', '#ff6b6b'],
        tooltip: {
          trigger: 'item',
          formatter: p => {
            const pct = total > 0 ? (p.percent).toFixed(1) : '0'
            return `${p.name}: ${p.value} 场 (${pct}%)`
          }
        },
        legend: {
          bottom: 0,
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          textStyle: { color: '#606266', fontSize: 12 }
        },
        series: [{
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '44%'],
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 3 },
          label: {
            formatter: '{b}\n{d}%',
            color: '#606266',
            fontSize: 12
          },
          labelLine: { length: 12, length2: 8 },
          data: [
            { value: won, name: '成功中标' },
            { value: total - won, name: '未中标' }
          ]
        }]
      })
    },
    handleResize() {
      if (this.winRateChartInst) this.winRateChartInst.resize()
      if (this.resultPieChartInst) this.resultPieChartInst.resize()
    }
  }
}
</script>

<style scoped lang="scss">
.doc-record {
  padding: 4px 0 20px;
}

.page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 14px;
}
.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #1f2d3d;
}
.page-desc {
  margin-top: 6px;
  font-size: 13px;
  color: #909399;
}
.page-head-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 统计卡片 */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}
.stat-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  padding: 18px 20px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(31, 45, 61, 0.07);
}
.stat-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}
.stat-value {
  display: flex;
  align-items: baseline;
  line-height: 1;
}
.stat-num {
  font-size: 26px;
  font-weight: 700;
  color: #1f2d3d;
  font-variant-numeric: tabular-nums;
}
.stat-unit {
  font-size: 13px;
  color: #909399;
  margin-left: 4px;
}
.stat-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
  flex-shrink: 0;
}
.tone-blue .stat-icon { background: #e6f2ff; color: #409eff; }
.tone-green .stat-icon { background: #e9f9ef; color: #36cf63; }
.tone-red .stat-icon { background: #fdeaea; color: #f56c6c; }
.tone-cyan .stat-icon { background: #e8f8f8; color: #13c2c2; }

/* 面板 */
.panel {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  margin-bottom: 20px;
  overflow: hidden;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
  border-bottom: 1px solid #f2f3f5;
}
.panel-head h3 {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
}
.panel-head h3::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 14px;
  border-radius: 2px;
  background: #409eff;
  margin-right: 8px;
  vertical-align: -2px;
}
.panel-note {
  font-size: $font-size-base;
  color: #b0b6c0;
}
.panel-body { padding: 18px 22px 8px; }

.chart-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.chart-lg { height: 300px; }

/* 分组表格 */
.group-project .group-name {
  color: #1f2d3d;
  font-weight: 600;
  line-height: 1.4;
}
.group-project .group-meta {
  margin-top: 4px;
  font-size: $font-size-base;
  color: #909399;
}
.group-meta i {
  margin-right: 3px;
}
.group-remark {
  color: #e6a23c;
}

.bidder-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 0;
}
.bidder-line {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 20px;
}
.bidder-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #409eff;
  flex-shrink: 0;
}
.bidder-dot.is-won { background: #36cf63; }
.bidder-name {
  font-size: 13px;
  color: #1f2d3d;
}
.bidder-name.is-won {
  color: #409eff;
  font-weight: 600;
}
.bidder-won-price {
  font-size: $font-size-base;
  color: #36cf63;
  font-weight: 600;
}
.price-list .bidder-line { justify-content: flex-end; }
.bidder-price {
  font-size: 13px;
  color: #606266;
  font-variant-numeric: tabular-nums;
}
.bidder-price.is-won {
  color: #36cf63;
  font-weight: 700;
}
.win-amount {
  color: #36cf63;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.date-cell {
  color: #606266;
  font-size: $font-size-base;
}

.pager-wrap {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
}
.table-empty {
  padding: 36px 0;
  color: #c0c4cc;
  font-size: 13px;
}
.table-empty i {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}

@media (max-width: 1200px) {
  .stat-grid { grid-template-columns: repeat(2, 1fr); }
  .chart-grid { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .stat-grid { grid-template-columns: 1fr; }
}
</style>
